import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useResident } from '../lib/useResident.jsx';
import { normalizeLoginCode } from '../lib/loginCode.js';
import './Login.css';

// Resident sign-in page. Big, senior-friendly input. Auto-uppercases
// as the resident types. Forgives missing hyphens (MARGARET742 works
// the same as MARGARET-742). The lookup against /brain_health/login_index/
// happens in useResident.signIn().
//
// Secondary link at the bottom redirects to /signup for users who
// arrived here by mistake — typically family members who subscribed
// to the individual plan and clicked "Resident sign in" instead of
// "Individual sign in" from the masthead dropdown.
export default function Login() {
  const { isSignedIn, signIn } = useResident();
  const navigate = useNavigate();
  const location = useLocation();

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const returnTo = location.state?.returnTo || '/resident';

  // Already signed in? Skip the form and land on the dashboard.
  useEffect(() => {
    if (isSignedIn) {
      navigate(returnTo, { replace: true });
    }
  }, [isSignedIn, navigate, returnTo]);

  // As the resident types, show the normalized form so they see
  // exactly what'll be looked up. This is more reassuring than
  // a hidden transform — they see "MARGARET-742" form as they type.
  function handleChange(e) {
    const raw = e.target.value;
    // Live-format: uppercase + insert hyphen between letters and digits.
    // Strip everything that isn't a letter, digit, or the hyphen we add.
    const cleaned = raw.toUpperCase().replace(/[^A-Z0-9-]/g, '');
    // Avoid double-hyphens or trailing hyphens during typing
    const tidied = cleaned.replace(/--+/g, '-').replace(/^-/, '');
    setCode(tidied);
    if (error) setError(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(code);
      // useEffect above handles the navigation once isSignedIn flips.
    } catch (err) {
      setError(err.message || 'Sign-in failed.');
      setSubmitting(false);
    }
  }

  return (
    <div className="container login-page">
      <span className="eyebrow fade-in-1">Resident Sign In</span>
      <h1 className="login-title fade-in-2">
        Enter your <em>sign-in code</em>.
      </h1>
      <p className="login-lede fade-in-3">
        Your community gave you a card with a code that looks something
        like <span className="login-example">MARGARET-742</span>. Type it
        below.
      </p>

      <form onSubmit={handleSubmit} className="login-form fade-in-4">
        <label htmlFor="resident-code" className="login-label">
          Sign-in code
        </label>
        <input
          id="resident-code"
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          value={code}
          onChange={handleChange}
          placeholder="MARGARET-742"
          className="login-input"
          disabled={submitting}
          autoFocus
        />

        {error ? (
          <div className="login-error" role="alert">
            <p className="login-error-line">Code not found — please check your card and try again.</p>
            <p className="login-error-help">
              If you still can't sign in, ask a staff member for help.
            </p>
          </div>
        ) : null}

        <button
          type="submit"
          className="btn login-submit"
          disabled={submitting || !code}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="login-alternative fade-in-4">
        Not a resident?{' '}
        <Link to="/signup" className="login-alternative-link">
          Sign in as an individual subscriber →
        </Link>
      </p>
    </div>
  );
}
